import { useMemo } from 'react';

import {
  VENUES_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';

import { FacetChildKey } from '~/types/facets';
import { OptionFacetCategoryType, OptionFacetType } from '~/types/types';

import {
  CAPACITY_FILTER,
  createAwardGroupsFilter,
  createLocationFilter,
  createPricingFilter,
} from '../../../../meta/facets';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { SearchResultsPageFilter, filtersByVendorCategory } from '../../meta/searchCategoriesMeta';
import { VendorSearchRequest } from '../../types/types';
import { createManualSortingFilter } from '../ManualSortingOptions/ManualSortingOptions';

interface FilterLookup {
  [facetKey: string]: OptionFacetCategoryType;
  price: OptionFacetCategoryType;
  capacity: OptionFacetCategoryType;
  availability: OptionFacetCategoryType;
}

/**
 * Sorts the child filters using the definition in 'searchCategoriesMeta'
 */
function sortChildFilters(
  filterLookup: FilterLookup,
  { childOrder, key }: SearchResultsPageFilter
): OptionFacetType[] {
  const facets = filterLookup[key]?.facets || [];

  if (!childOrder) {
    return facets;
  }

  return [...facets]?.sort(
    (facetA, facetB) =>
      childOrder.indexOf(facetA.key as FacetChildKey) -
      childOrder.indexOf(facetB.key as FacetChildKey)
  );
}

const convertCategoryListToFilters = (
  categoryList: SearchResultsPageFilter[],
  filterLookup: FilterLookup
): OptionFacetCategoryType[] => {
  return categoryList
    .map((categoryName) => ({
      ...filterLookup[categoryName.key],
      dependsOnKey: categoryName.dependsOnKey,
      isImportant: categoryName.isImportant,
      facets: sortChildFilters(filterLookup, categoryName),
    }))
    .filter((filter) => !!filter.key);
};

// Removes the location filter if it's not a city search
const removeUnusedFilters = (filters: SearchResultsPageFilter[], isCity: boolean) =>
  filters.filter((filter) => {
    if (filter.key === 'location' && !isCity) {
      return false;
    }
    return true;
  });

/**
 * Creates a list of available filters for searching. This list is used for the filters top bar and the side drawer.
 *
 * This should all be able to move to the server
 */
export const useSearchCategories = (
  vendorSearch: VendorSearchRequest | null | undefined
): OptionFacetCategoryType[] => {
  const { facetsByVendorTypeAndCategory } = useSearchResultsPageContext();
  // This bit takes our meta-list of display names and turns them into a filter/category
  return useMemo(() => {
    const { selectedLocation, selectedTaxonomy } = vendorSearch || {};
    const taxonomyKey = (selectedTaxonomy?.key ||
      VENUES_TAXONOMY_KEY) as SearchableVendorTaxonomyKey;
    const isCity = selectedLocation?.type === 'CITY';

    const allFilters = removeUnusedFilters(filtersByVendorCategory(taxonomyKey), isCity);

    // Now, transform our facets for vendor type to a lookup by display name
    const filterLookup: FilterLookup = (facetsByVendorTypeAndCategory[taxonomyKey] || []).reduce(
      (result, category) => ({
        ...result,
        [category.key]: category,
      }),
      {
        'award-groups': createAwardGroupsFilter(),
        sorting: createManualSortingFilter(vendorSearch),
        location: createLocationFilter(taxonomyKey),
        price: createPricingFilter(),
        capacity: CAPACITY_FILTER,
        availability: {
          slug: 'availability-date',
          displayName: 'Availability',
          key: 'availability',
        },
      }
    );

    return convertCategoryListToFilters(allFilters, filterLookup);
  }, [vendorSearch, facetsByVendorTypeAndCategory]);
};
